import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import upload from "../assests/images/upload.svg";
import Resumable from "resumablejs";
import { Alert } from "@mui/material";
import axios from "../api/axios";

export default function Upload(props) {
  const { isDesktop } = useResponsiveFlags();
  const [desktopDevice, setDesktopDevice] = useState(true);

  const [errorMessage, setErrorMessage] = useState(props.err ? props.err : "");
  const [successMessage, setSuccessMessage] = useState("");

  const [filesMetaData, setFilesMetaData] = useState([]);
  const [isGlobalUploadDisabled, setIsGlobalUploadDisabled] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newTotalFilesSize, setNewTotalFilesSize] = useState(props.size);
  const [progressMap, setProgressMap] = useState({});
  const [uploadingMap, setUploadingMap] = useState({});
  const resumableInstances = useRef({});
  const existingFiles = props.existingFiles || [];
  useEffect(() => {
    setNewTotalFilesSize(props.size);
  }, [props.size]);

  useEffect(() => {
    setDesktopDevice(
      !/Mobi|Android|iPhone|iPad|Tablet/i.test(navigator.userAgent)
    );
  }, [isDesktop]);

  useEffect(() => {
    if (filesMetaData.length === 0) {
      setShowModal(false);
      setErrorMessage("");
      setProgressMap({});
      setIsGlobalUploadDisabled(false);
      setUploadingFiles(false);
      props.refreshData();
      return;
    }
    if (!uploadingFiles) {
      const hasError = filesMetaData.some(
        (file) =>
          file.errors?.invalidFileName ||
          file.errors?.invalidFileType ||
          file.errors?.sizeError ||
          file.errors?.aliasExists ||
          file.errors?.nameExists ||
          file.errors?.sampleCountError
      );
      setIsGlobalUploadDisabled(hasError);
    }
  }, [filesMetaData, uploadingFiles]);

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        if (successMessage) {
          setSuccessMessage("");
        } else if (errorMessage) {
          setErrorMessage("");
        }
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  function countSamplesInFile(file) {
    return new Promise((resolve, reject) => {
      const blob = file.slice(0, 10 * 1024 * 1024);
      const reader = new FileReader();

      reader.onload = function (event) {
        const content = event.target.result;
        const firstLine = content.split("\n")[0];
        const columns = firstLine.split("\t");
        const sampleCount = columns.length ? columns.length - 1 : 0;
        resolve(sampleCount);
      };

      reader.onerror = reject;
      reader.readAsText(blob);
    });
  }
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleClick = () => {
    const fileInput = document.getElementById("fileInput");
    fileInput.click();
  };

  const removeFileById = (uploadId) => {
    const fileToRemove = filesMetaData.find((f) => f.uploadId === uploadId);
    if (!fileToRemove) return;

    const resumable = resumableInstances.current[uploadId];
    if (resumable) {
      resumable.cancel();
      delete resumableInstances.current[uploadId];
    }

    setProgressMap((prev) => {
      const updated = { ...prev };
      delete updated[uploadId];
      return updated;
    });

    if (
      !(
        fileToRemove.errors?.invalidFileName ||
        fileToRemove.errors?.invalidFileType ||
        fileToRemove.errors?.sizeError ||
        fileToRemove.errors?.aliasExists ||
        fileToRemove.errors?.nameExists ||
        fileToRemove.errors?.sampleCountError
      )
    ) {
      setNewTotalFilesSize((prev) => Math.max(prev - fileToRemove.size, 0));
    }

    setFilesMetaData((prev) =>
      prev.filter((file) => file.uploadId !== uploadId)
    );
  };

  const cancelUploadAll = () => {
    Object.values(resumableInstances.current).forEach((resumable) => {
      resumable.cancel();
    });

    resumableInstances.current = {};

    setProgressMap({});
    setShowModal(false);
    setFilesMetaData([]);
    setErrorMessage("");
    setIsGlobalUploadDisabled(false);
    setUploadingFiles(false);
    props.refreshData();
  };

  const handleFormDataChange = (index, field, value) => {
    const updatedFiles = filesMetaData.map((file, i) => {
      if (i !== index) return file;

      const wasValid =
        !file.errors?.invalidFileName &&
        !file.errors?.invalidFileType &&
        !file.errors?.sizeError &&
        !file.errors?.aliasExists &&
        !file.errors?.nameExists &&
        !file.errors?.sampleCountError;

      let updatedFile = {
        ...file,
        [field]: value,
      };

      if (field === "alias") {
        const aliasExistsInDB = existingFiles.some(
          (existing) => existing.title === value
        );

        const aliasExistsInNewFiles = filesMetaData.some(
          (f, i) => i !== index && f.alias === value
        );

        const aliasExists = aliasExistsInDB || aliasExistsInNewFiles;

        updatedFile.errors = {
          ...updatedFile.errors,
          aliasExists,
        };
      }

      const isValid =
        !updatedFile.errors?.invalidFileName &&
        !updatedFile.errors?.invalidFileType &&
        !updatedFile.errors?.sizeError &&
        !updatedFile.errors?.aliasExists &&
        !updatedFile.errors?.nameExists &&
        !updatedFile.errors?.sampleCountError;

      // 🔥 Adjust size based on validity change
      if (wasValid && !isValid) {
        setNewTotalFilesSize((prev) => Math.max(prev - file.size, 0));
      } else if (!wasValid && isValid) {
        setNewTotalFilesSize((prev) => prev + file.size);
      }

      return updatedFile;
    });
    setFilesMetaData(updatedFiles);
  };
  const getFileExtension = (fileName) => {
    return fileName.includes(".")
      ? fileName.split(".").pop().toLowerCase()
      : "";
  };

  const isValidFileName = (fileName) => {
    // Only letters, numbers, dash, underscore
    // No spaces or other special chars
    return /^[A-Za-z0-9._-]+\.[A-Za-z0-9]+$/.test(fileName);
  };

  const isValidFileType = (extension) => {
    return ["txt", "tsv"].includes(extension);
  };

  const processFiles = async (fileList) => {
    const filesArray = Array.from(fileList);
    const newMetadata = [];
    let fileSize = 0;

    for (const file of filesArray) {
      const extension = getFileExtension(file.name);
      const validName = isValidFileName(file.name);
      const validType = isValidFileType(extension);

      const sampleCount = validType ? await countSamplesInFile(file) : 0;

      const nameExists = existingFiles.some(
        (existing) => existing.name === file.name
      );

      const sizeError =
        1024 * 1024 * 1024 < fileSize + newTotalFilesSize + file.size;
      newMetadata.push({
        uploadId: Date.now().toString() + Math.random().toString(36),
        file,
        name: file.name,
        size: file.size,
        type: extension,
        alias: "",
        fileType: "Bulk Expression File",
        sampleCount,
        progress: 0,
        errors: {
          invalidFileName: !validName,
          invalidFileType: !validType,
          nameExists,
          aliasExists: false,
          sampleCountError: validType ? sampleCount > 1000 : false,
          sizeError,
        },
      });

      if (
        validName &&
        validType &&
        !sizeError &&
        !nameExists &&
        sampleCount <= 1000
      ) {
        fileSize += file.size;
      }
    }

    setNewTotalFilesSize((prev) => prev + fileSize);
    setFilesMetaData((prev) => [...prev, ...newMetadata]);
    setShowModal(true);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!event.dataTransfer.files.length) return;
    await processFiles(event.dataTransfer.files);
  };

  const handleFileChange = async (event) => {
    if (!event.target.files.length) return;
    await processFiles(event.target.files);
  };

  const uploadAllFiles = async () => {
    setUploadingFiles(true);

    const validFiles = filesMetaData.filter(
      (file) =>
        !file.errors?.invalidFileName &&
        !file.errors?.invalidFileType &&
        !file.errors?.sizeError &&
        !file.errors?.aliasExists &&
        !file.errors?.nameExists &&
        !file.errors?.sampleCountError
    );

    await Promise.allSettled(
      validFiles.map((file) => uploadFileById(file.uploadId))
    );

    setUploadingFiles(false);
  };
  const uploadFileById = async (uploadId) => {
    const fileMeta = filesMetaData.find((f) => f.uploadId === uploadId);
    if (!fileMeta) return;

    setUploadingMap((prev) => ({
      ...prev,
      [uploadId]: true,
    }));

    try {
      await uploadFileWithResumable(fileMeta, (progress) => {
        setProgressMap((prev) => ({
          ...prev,
          [uploadId]: progress,
        }));
      });

      removeFileById(uploadId);
      setSuccessMessage("Upload successful");
    } catch (error) {
      setErrorMessage(
        typeof error === "string" ? error : error?.message || "Upload failed"
      );
      setTimeout(() => {
        removeFileById(uploadId);
      }, 3000);
    } finally {
      setUploadingMap((prev) => {
        const updated = { ...prev };
        delete updated[uploadId];
        return updated;
      });
    }
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(";").shift());
    }
  };
  const uploadFileWithResumable = async (fileMeta, onProgress) => {
    // Ensure CSRF cookie exists
    await axios.get("/sanctum/csrf-cookie", {
      withCredentials: true,
    });

    const token = getCookie("XSRF-TOKEN");

    if (!token) {
      throw new Error("CSRF token missing. Refresh page.");
    }

    return new Promise((resolve, reject) => {
      const resumable = new Resumable({
        target: "/backend/api/upload-file",
        chunkSize: 8 * 1024 * 1024,
        testChunks: false,
        simultaneousUploads: 1,
        maxChunkRetries: 0,
        permanentErrors: [400, 401, 403, 404, 409, 422, 500],
        withCredentials: true,

        // 🔥 THIS IS THE CRITICAL FIX
        headers: {
          "X-XSRF-TOKEN": token,
          "X-Requested-With": "XMLHttpRequest",
        },

        query: {
          title: fileMeta.alias || fileMeta.name,
          fileType: fileMeta.fileType,
          sampleCount: fileMeta.sampleCount,
        },
      });

      resumableInstances.current[fileMeta.uploadId] = resumable;
      resumable.on("fileAdded", function (file) {
        resumable.upload();
      });

      resumable.on("fileProgress", function (file) {
        const progress = Math.floor(file.progress() * 100);
        if (onProgress) onProgress(progress);
      });

      resumable.on("fileSuccess", function () {
        delete resumableInstances.current[fileMeta.uploadId];
        resolve();
      });

      resumable.on("fileError", function (file, message) {
        delete resumableInstances.current[fileMeta.uploadId];

        let errorMessage = "Upload failed";

        try {
          const parsed = JSON.parse(message);
          if (parsed.error) {
            errorMessage = parsed.error;
          }
        } catch (e) {
          if (typeof message === "string") {
            errorMessage = message;
          }
        }

        resumable.cancel(); // 🔥 IMPORTANT - stop upload

        reject(errorMessage);
      });

      resumable.addFile(fileMeta.file);
    });
  };
  return (
    <>
      <div
        onDrop={handleDrop}
        onDragOver={desktopDevice ? handleDragOver : undefined}
      >
        <div className="d-flex flex-column-reverse flex-xl-row justify-content-between pt-2">
          <span className={`custom-title mb-3 mb-xl-0`}>Instructions</span>
          <div className="storage-bar pt-1 pt-xl-2 mb-3 mb-xl-0">
            <p className="d-flex justify-content-between">
              <span className="storage-limit">Storage Limit</span>{" "}
              <span>{props.size} MB / 1000 MB</span>
            </p>
            <div className="progress-bar">
              <div
                className="progress-files"
                style={{ width: `${props.size / 10}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="d-xl-flex">
          <div className="flex-grow-1 p-1 p-xl-0">
            <div className="border-0 border-bottom border-top">
              <button
                className="instruction-heading accordion-button collapsed no-focus-outline d-flex justify-content-between align-items-center p-0 pe-xl-3"
                style={{ margin: "8px 0" }}
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseFileNames"
                aria-expanded="false"
                aria-controls="collapseFileNames"
              >
                File Names
                <span
                  className="icon-toggle ms-auto ps-3 home-card-title"
                  style={{ fontSize: "32px" }}
                ></span>
              </button>
              <div
                id="collapseFileNames"
                className="accordion-collapse collapse pe-4 pe-xl-5"
                aria-labelledby="headingFileNames"
              >
                <div>
                  <ul>
                    <li>Dashes (-) and underscores (_) are supported.</li>
                    <li>
                      Any other special characters (e.g. ~ ` ! @ # $ % ^ & *
                      &#40; &#41; &#123; | &#125; / \ : ; &quot; &apos; &lt;
                      &gt; ? , = +) and white spaces are not allowed.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-0 border-bottom">
              <button
                className="instruction-heading accordion-button collapsed no-focus-outline d-flex justify-content-between align-items-center p-0 pe-xl-3"
                style={{ margin: "8px 0" }}
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseBulkFile"
                aria-expanded="false"
                aria-controls="collapseBulkFile"
              >
                Prepare a Bulk Expression File
                <span
                  className="icon-toggle ms-auto ps-3 home-card-title"
                  style={{ fontSize: "32px" }}
                ></span>
              </button>
              <div
                id="collapseBulkFile"
                className="accordion-collapse collapse pe-4 pe-xl-5"
                aria-labelledby="headingBulkFile"
              >
                <div>
                  <ol>
                    <li>
                      The file must be in a
                      <strong> tab-delimited text format</strong>, with gene
                      names as the row name and sample names as the column name.
                      Other formats (such as csv) are not allowed.
                    </li>
                    <li>
                      The <strong> sample names must be unique.</strong>
                    </li>
                    <li>
                      Number of samples should not exceed more than 1000. If you
                      would like to analyze more than 1000 samples, split the
                      samples.
                    </li>
                    <li>
                      Gene names must be{" "}
                      <strong>
                        HUGO-compliant gene names. Ensembl IDs cannot be used.{" "}
                      </strong>
                      If multiple Ensembl IDs are converted to a single gene
                      name, sum the expression values.
                    </li>
                    <li>
                      For RNA-Seq data, the
                      <strong> expression values must be in TPM </strong>
                      (transcripts per million), non-log,
                      <strong> without any missing values.</strong>
                    </li>
                    <li>
                      For microarray data, iSort will apply quantile
                      normalization.
                    </li>
                    <li>
                      If your data set has significant batch differences (i.e,
                      different library prep methods, different tissue types,
                      etc.), separate the bulk expression file accordingly and
                      run iSort separately.
                    </li>
                    <li>
                      There must be at least 3 samples in a bulk expression
                      file. 5 or more is ideal to effectively apply batch
                      correction.
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="border-0 border-bottom">
              <button
                className="instruction-heading accordion-button collapsed no-focus-outline d-flex justify-content-between align-items-center p-0 pe-xl-3"
                style={{ margin: "8px 0" }}
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseCellFile"
                aria-expanded="false"
                aria-controls="collapseCellFile"
              >
                Prepare a Cell Proportion File (for iSort HiRes only)
                <span
                  className="icon-toggle ms-auto ps-3 home-card-title"
                  style={{ fontSize: "32px" }}
                ></span>
              </button>
              <div
                id="collapseCellFile"
                className="accordion-collapse collapse pe-4 pe-xl-5"
                aria-labelledby="headingCellFile"
              >
                <div>
                  <ul>
                    <li>
                      iSort HiRes is currently offered as a stand-alone tool.
                      Browser version will become available soon.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-0 border-bottom">
              <button
                className="instruction-heading accordion-button collapsed no-focus-outline d-flex justify-content-between align-items-center p-0 pe-xl-3"
                style={{ margin: "8px 0" }}
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseAlias"
                aria-expanded="false"
                aria-controls="collapseAlias"
              >
                File Name Alias
                <span
                  className="icon-toggle ms-auto ps-3 home-card-title"
                  style={{ fontSize: "32px" }}
                ></span>
              </button>
              <div
                id="collapseAlias"
                className="accordion-collapse collapse pe-4 pe-xl-5"
                aria-labelledby="headingAlias"
              >
                <div>
                  <ul>
                    <li>
                      <strong>File Name Alias (optional):</strong>
                      <br /> You can specify short file name alias (another file
                      name) for convenience.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div
            className="file-upload-section"
            onClick={desktopDevice ? handleClick : undefined}
          >
            <p
              className="custom-title"
              style={{ lineHeight: "150%", marginTop: "6px" }}
            >
              Upload Files
            </p>
            <div className="upload-box">
              <img src={upload} alt="" className="mt-3" />
              {desktopDevice ? (
                <>
                  <p className="p-2 text-center">
                    <u>Click to Upload</u> or
                    <br />
                    Drag and drop files here
                  </p>
                  <p className="review-text">1000 samples max</p>
                </>
              ) : (
                <p className="review-text storage-limit p-2">
                  Please use a desktop machine to upload file(s).
                </p>
              )}
            </div>
            <input
              type="file"
              id="fileInput"
              style={{ display: "none" }}
              onChange={handleFileChange}
              multiple
            />
          </div>

          <Modal
            show={showModal}
            centered
            backdrop="static"
            keyboard={false}
            dialogClassName="upload-modal"
            contentClassName="p-4"
          >
            <Modal.Header className="p-0 border-0">
              <p className="sub-step-bold mb-0">File Details</p>
            </Modal.Header>
            <Modal.Body className="mt-4 mb-4 p-0">
              {errorMessage && (
                <div id="error-message" className="alert alert-danger">
                  {errorMessage}
                </div>
              )}
              {successMessage && (
                <div id="success-message" className="alert alert-success">
                  {successMessage}
                </div>
              )}
              {filesMetaData.map((file, index) => (
                <div
                  key={file.uploadId}
                  className="file-details mb-3  border-bottom"
                >
                  {file.errors?.invalidFileName && (
                    <div className="text-danger">
                      Invalid file name. Only letters, numbers, dash (-) and
                      underscore (_) are allowed. No spaces or special
                      characters.
                    </div>
                  )}
                  {file.errors?.invalidFileType && (
                    <div className="text-danger">
                      File type not allowed. Only .txt or .tsv files are
                      allowed.
                    </div>
                  )}
                  {file.errors?.sampleCountError && (
                    <div className="text-danger">
                      File contains too many samples. Maximum allowed is 1000.
                    </div>
                  )}
                  {file.errors?.nameExists && (
                    <div className="text-danger">
                      A file with this name already exists.
                    </div>
                  )}
                  {file.errors?.aliasExists && (
                    <div className="text-danger">
                      This alias is already in use.
                    </div>
                  )}
                  {file.errors?.sizeError && (
                    <div className="text-danger">
                      Adding this file Exceeds the limits of per user file
                      upload limits.
                    </div>
                  )}

                  <p>
                    <span className="form-label">File Name:</span> {file.name}
                  </p>
                  <div className="row align-items-start mb-3">
                    <div className="col-lg-3 mb-3 mb-lg-0">
                      <Form.Label>File Name Alias:</Form.Label>
                      <Form.Control
                        type="text"
                        value={file.alias}
                        disabled={uploadingMap[file.uploadId] || uploadingFiles}
                        onChange={(e) =>
                          handleFormDataChange(index, "alias", e.target.value)
                        }
                        placeholder="Enter alias"
                      />
                    </div>

                    <div className="col-lg-3 mb-3 mb-lg-0">
                      <Form.Label>File Type:</Form.Label>
                      <Form.Select
                        value={file.fileType}
                        disabled={uploadingMap[file.uploadId] || uploadingFiles}
                        onChange={(e) =>
                          handleFormDataChange(
                            index,
                            "fileType",
                            e.target.value
                          )
                        }
                        required
                      >
                        <option value="Bulk Expression File">
                          Bulk Expression File
                        </option>
                      </Form.Select>
                    </div>

                    <div className="col-lg-3 mb-3 mb-lg-0">
                      <Form.Label>
                        {(file.size / 1000).toFixed(2)} KB
                      </Form.Label>
                      <div
                        className="d-flex align-items-center"
                        style={{ padding: "14px 0" }}
                      >
                        <div className="progress-bar-dialog">
                          <div
                            className="progress-files"
                            style={{
                              width: `${progressMap[file.uploadId] || 0}%`,
                              // }}
                              // style={{
                              //   width: `${file.progress || 0}%`,
                              height: "8px",
                              backgroundColor: "#007bff",
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 mb-3 mb-lg-0 d-flex flex-column">
                      <button
                        onClick={() => uploadFileById(file.uploadId)}
                        disabled={
                          uploadingMap[file.uploadId] ||
                          uploadingFiles ||
                          file.errors?.invalidFileType ||
                          file.errors?.invalidFileName ||
                          file.errors?.sampleCountError ||
                          file.errors?.nameExists ||
                          file.errors?.aliasExists ||
                          file.errors?.sizeError
                        }
                        className="upload-file-btn me-0 mb-2"
                      >
                        Upload
                      </button>
                      <button
                        onClick={() => removeFileById(file.uploadId)}
                        className="cancel-btn me-0"
                        style={{ padding: "4px 20px" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
                // </div>
              ))}
            </Modal.Body>
            <Modal.Footer className="justify-content-center border-0 p-0">
              <button
                className="cancel-btn m-0 me-2"
                style={{ padding: "4px 20px" }}
                onClick={handleClick}
                disabled={uploadingFiles || (Array.isArray(uploadingMap)
                  ? uploadingMap.length > 0
                  : Object.keys(uploadingMap || {}).length > 0)}
              >
                <input
                  type="file"
                  id="fileInput"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  multiple
                />
                Add File
              </button>
              <button
                className="cancel-btn m-0 mx-2"
                style={{ padding: "4px 20px" }}
                onClick={uploadAllFiles}
                disabled={isGlobalUploadDisabled || uploadingFiles || (Array.isArray(uploadingMap)
                  ? uploadingMap.length > 0
                  : Object.keys(uploadingMap || {}).length > 0)}
              >
                Upload All
              </button>
              <button
                className="cancel-btn m-0 ms-2"
                style={{ padding: "4px 20px" }}
                onClick={cancelUploadAll}
              >
                Cancel
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
}
